import { contentCopy } from '@/copy'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import type { IconName } from '../Icon/icons'
import styles from './Chip.module.css'

type ChipProps = { label: string; icon?: IconName; className?: string } & (
  | { variant: 'kind' }
  | {
      variant: 'filter'
      pressed: boolean
      count?: number
      /** `surface` (a card, an overlay) hovers and presses to raise (D190). */
      ground?: 'page' | 'surface'
      /** `default` in a toolbar, beside fields or buttons: the row's height (D212). */
      size?: 'compact' | 'default'
      onPressedChange: (pressed: boolean) => void
    }
  | { variant: 'tag'; onRemove?: () => void }
)

export function Chip(props: ChipProps) {
  const { label, icon, className } = props
  const glyph = icon && <Icon name={icon} size="icon-embedded" />

  if (props.variant === 'filter') {
    const {
      pressed,
      onPressedChange,
      count,
      ground = 'page',
      size = 'compact',
    } = props
    return (
      <button
        type="button"
        className={cx(styles.filter, className)}
        data-ground={ground}
        data-size={size}
        aria-pressed={pressed}
        onClick={() => onPressedChange(!pressed)}
      >
        {pressed && (
          <Icon name="check" size="icon-embedded" className={styles.check} />
        )}
        {glyph}
        <span>{label}</span>
        {count != null && (
          <>
            {/* Whitespace-only text is not rendered in the flex row, but it
                keeps the accessible name "All 9", not "All9". */}{' '}
            <span className={styles.count}>{count}</span>
          </>
        )}
      </button>
    )
  }

  return (
    <span className={cx(styles.chip, className)} data-variant={props.variant}>
      {glyph}
      {label}
      {props.variant === 'tag' && props.onRemove && (
        <button
          type="button"
          className={styles.remove}
          aria-label={contentCopy.chip.remove(label)}
          onClick={props.onRemove}
        >
          <Icon name="cross-2" size="icon-embedded" />
        </button>
      )}
    </span>
  )
}
